import React, { ReactNode, RefObject, useEffect, useRef } from "react";
import { useApiErrorHandler } from "../../hooks/useApiErrorHandler";
import DialogBody from "./DialogBody";
import DialogFooter from "./DialogFooter";
import DialogHeader from "./DialogHeader";

interface DialogProps {
  id: string;
  isFullScreen?: boolean;
  headerText: string;
  body: ReactNode;
  submitText?: string;
  onSubmit?: () => Promise<unknown>;
  onSuccess?: () => void;
  onFailure?: (error: string) => void;
  onRefChange?: (ref: RefObject<HTMLDivElement>) => void;
}

export default function Dialog(
  {
    id,
    isFullScreen = false,
    headerText,
    body,
    submitText,
    onSubmit,
    onSuccess,
    onFailure,
    onRefChange,
  }: DialogProps,
) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const { loading, error, fetchData } = useApiErrorHandler();

  useEffect(() => {
    onRefChange && onRefChange(dialogRef);
  }, [onRefChange, dialogRef]);

  useEffect(() => {
    if (loading) return;
    if (error) {
      onFailure && onFailure(error);
    }
    else {
      onSuccess && onSuccess();
    }
  }, [loading, error, onFailure, onSuccess]);

  return (
    <div
      ref={dialogRef}
      className="modal fade"
      id={id}
      aria-labelledby={`${id}-header`}
    >
      <div className={`modal-dialog modal-dialog-centered modal-dialog-scrollable
        ${isFullScreen ? "modal-fullscreen p-5" : ""}`}
      >
        <div className="modal-content rounded">
          <DialogHeader id={`${id}-header`}>{headerText}</DialogHeader>
          <DialogBody>{body}</DialogBody>
          <DialogFooter
            submitText={submitText}
            onSubmit={() => onSubmit && fetchData(onSubmit())}
          />
        </div>
      </div>
    </div>
  );
}
